import RowStack from "@common-components/common/row-stack/RowStack";
import { MoreVert } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import React, { useState } from 'react';
import NodeMenu from "../bot-components/node-menu";
import classes from "../bt-chatbot.module.scss";
import { nodeTypes, secondaryNodesThatLookLikeMainNodes } from "./bt-nodes-def";
import { NodeType } from "@common-models/bot-node";
import { useAppSelector } from "@common/reducers/hooks/store.hook";
import { getIsFromNodeEventSelector } from "@common/reducers/ChatbotSelectors";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { namespace } from "@common/config/constants";

type Props = {
    type: NodeType;
    id: string;
}

export default function NodeHeader({ type, id }: Props) {
    const { t } = useTranslation(namespace);
    const { sessionId } = useParams<{ sessionId: string }>();
    const isFromNodeEvent = useAppSelector(state => getIsFromNodeEventSelector(state, id, sessionId));
    const isReadOnly = useAppSelector(state => state.ChatbotReducer.isReadOnly);

    const [menuOpen, setMenuOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const nodeType = nodeTypes?.[type]
    const NodeIcon = nodeType?.nodeIcon
    const isMainNode = nodeType?.isMainNode || secondaryNodesThatLookLikeMainNodes.includes(type);

    const title = () => {
        switch (type) {
            case 'sendMessage':
                return t('chatbot.nodeTypes.sendMessage.shortTitle');
            case 'question':
                return t('chatbot.nodeTypes.question.shortTitle');
            case 'condition':
                return t('chatbot.nodeTypes.condition.shortTitle');
            case 'webhook':
                return t('chatbot.nodeTypes.webhook.shortTitle');
            case 'googleSheets':
                return t('chatbot.nodeTypes.googleSheets.shortTitle');
            case 'messageContact':
                return t('chatbot.nodeTypes.messageContact.shortTitle');
            case 'LLM':
                return t('chatbot.nodeTypes.LLM.shortTitle');
            case 'agent':
                return t('chatbot.nodeTypes.agent.shortTitle');
            default:
                return type;
        }
    }

    const handleOpenNodeMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuOpen((prev => !prev))
        setAnchorEl(event.currentTarget);
    };

    const handleCloseNodeMenu = () => {
        setMenuOpen(false);
        setAnchorEl(null);
    }

    const bgColor = () => {
        if (!isFromNodeEvent) return '#939393';
        return isMainNode ? nodeType?.color : 'white';
    }

    const iconBgColor = () => {
        if (!isFromNodeEvent) return 'var(--color-surface)';
        return isMainNode ? nodeType?.color : 'transparent';
    }

    const iconColor = () => {
        if (!isFromNodeEvent) return 'var(--color-text)';
        return isMainNode ? 'white' : nodeType?.color;
    }

    const titleColor = () => {
        if (!isFromNodeEvent) return 'var(--color-text)';
        return isMainNode ? 'white' : 'black';
    }

    return (
        <RowStack className={isMainNode ? classes.main_node_header : classes.secondary_node_header}
            sx={{
                backgroundColor: bgColor(),
                zIndex: 2,
                color: titleColor()
            }}>
            <div style={{
                backgroundColor: iconBgColor(),
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '7px',
                fontWeight: !isMainNode ? 700 : undefined,
            }} >
                {NodeIcon && <NodeIcon style={{ width: '25px', color: iconColor() }} />}
            </div>
            {!isMainNode && title()}
            {isMainNode && title()}
            <IconButton sx={{ p: 0, color: isMainNode ? 'white' : 'var(--bulk-text-secondary)', marginInlineStart: '5px' }}
                onClick={handleOpenNodeMenu} disabled={isReadOnly}>
                <MoreVert />
            </IconButton>
            <NodeMenu type={type} open={menuOpen} onClose={handleCloseNodeMenu} anchorEl={anchorEl} id={id} />
        </RowStack>
    )
}
